/**
 * CatalogSearchForm Component
 *
 * ASIN/EAN/Keyword search form for Amazon catalog lookup.
 */

import { useTranslation } from 'react-i18next'
import { Search, Loader2 } from 'lucide-react'
import { Card, CardContent } from '@/components/v2/ui/Card'
import { Button } from '@/components/v2/ui/Button'

const MARKETPLACES = [
  { value: 'de', label: 'Amazon.de' },
  { value: 'com', label: 'Amazon.com' },
  { value: 'co.uk', label: 'Amazon.co.uk' },
  { value: 'fr', label: 'Amazon.fr' },
  { value: 'it', label: 'Amazon.it' },
  { value: 'es', label: 'Amazon.es' },
]

interface CatalogSearchFormProps {
  query: string
  searchType: string
  marketplace: string
  isPending: boolean
  onQueryChange: (value: string) => void
  onSearchTypeChange: (value: string) => void
  onMarketplaceChange: (value: string) => void
  onSubmit: (e: React.FormEvent) => void
}

export function CatalogSearchForm({
  query,
  searchType,
  marketplace,
  isPending,
  onQueryChange,
  onSearchTypeChange,
  onMarketplaceChange,
  onSubmit,
}: CatalogSearchFormProps) {
  const { t } = useTranslation()

  const searchTypes = [
    { value: 'asin', label: 'ASIN' },
    { value: 'ean', label: 'EAN' },
    { value: 'keyword', label: t('amazonSpApi.lookup.keyword') },
  ]

  const placeholderMap: Record<string, string> = {
    asin: t('amazonSpApi.lookup.placeholderAsin'),
    ean: t('amazonSpApi.lookup.placeholderEan'),
    keyword: t('amazonSpApi.lookup.placeholderKeyword'),
  }

  return (
    <Card>
      <CardContent className="p-4">
        <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
              {t('amazonSpApi.lookup.searchLabel')}
            </label>
            <input
              type="text"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder={placeholderMap[searchType] || ''}
              className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
              {t('amazonSpApi.lookup.type')}
            </label>
            <select
              value={searchType}
              onChange={(e) => onSearchTypeChange(e.target.value)}
              className="rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200"
            >
              {searchTypes.map((st) => (
                <option key={st.value} value={st.value}>{st.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
              {t('amazonSpApi.marketplace')}
            </label>
            <select
              value={marketplace}
              onChange={(e) => onMarketplaceChange(e.target.value)}
              className="rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200"
            >
              {MARKETPLACES.map((mp) => (
                <option key={mp.value} value={mp.value}>{mp.label}</option>
              ))}
            </select>
          </div>
          <Button type="submit" variant="primary" size="sm" disabled={isPending || !query.trim()}>
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            {t('amazonSpApi.lookup.submit')}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
